import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Shield, Info } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Table from '../../components/ui/Table';
import { useToast } from '../../components/ui/Toast';
import api from '../../services/api';

export default function Settings() {
  const toast = useToast();
  const [, setSettings] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '' });

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [settingsRes, auditRes] = await Promise.allSettled([
          api.get('/v1/settings'),
          api.get('/v1/settings/audit?page=1&per_page=50'),
        ]);
        if (settingsRes.status === 'fulfilled' && settingsRes.value?.data) setSettings(settingsRes.value.data);
        if (auditRes.status === 'fulfilled' && auditRes.value?.data) setAuditLog(auditRes.value.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const handleChangePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) {
      toast('Заполните оба поля', 'error');
      return;
    }
    try {
      await api.put('/v1/auth/password', passwords);
      toast('Пароль изменён', 'success');
      setPasswords({ current_password: '', new_password: '' });
    } catch (e) {
      toast(e.message || 'Ошибка смены пароля', 'error');
    }
  };

  const auditColumns = [
    { key: 'created_at', label: 'Время', render: v => <span style={{ fontSize: 12, color: '#94A3B8' }}>{v ? new Date(v).toLocaleString('ru-RU') : '\u2014'}</span> },
    { key: 'user', label: 'Пользователь', render: v => <span style={{ fontWeight: 500 }}>{v || '\u2014'}</span> },
    { key: 'action', label: 'Действие', render: v => <Badge variant={v === 'CREATE' ? 'success' : v === 'DELETE' ? 'error' : 'primary'}>{v || '\u2014'}</Badge> },
    { key: 'entity', label: 'Объект', render: v => v || '\u2014' },
    { key: 'detail', label: 'Описание', render: v => <span style={{ fontSize: 13 }}>{v || '\u2014'}</span> },
  ];

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <span style={{ color: '#94A3B8', fontSize: 13 }}>Загрузка...</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, background: '#E6FAFB',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <SettingsIcon size={20} color="#009995" />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Настройки</h1>
      </div>

      {/* Change password */}
      <Card title="Смена пароля" extra={<Shield size={16} color="#009995" />}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
          <Input label="Текущий пароль" type="password" value={passwords.current_password}
            onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))} />
          <Input label="Новый пароль" type="password" value={passwords.new_password}
            onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} />
          <Button onClick={handleChangePassword} style={{ alignSelf: 'flex-start' }}>Сменить пароль</Button>
        </div>
      </Card>

      {/* System info */}
      <Card title="Информация о системе" extra={<Info size={16} color="#94A3B8" />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 13 }}>
          <div style={{ padding: '12px 16px', background: '#F8FAFC', borderRadius: 10 }}>
            <span style={{ color: '#94A3B8', fontSize: 12, fontWeight: 500 }}>Версия</span>
            <div style={{ fontWeight: 700, marginTop: 2, color: '#0F172A' }}>2.0.0</div>
          </div>
          <div style={{ padding: '12px 16px', background: '#F8FAFC', borderRadius: 10 }}>
            <span style={{ color: '#94A3B8', fontSize: 12, fontWeight: 500 }}>Среда</span>
            <div style={{ fontWeight: 700, marginTop: 2, color: '#0F172A' }}>Production</div>
          </div>
        </div>
      </Card>

      {/* Audit log */}
      <Card title="Журнал аудита" padding="0">
        <Table columns={auditColumns} data={auditLog} emptyText="Нет записей аудита" pageSize={10} />
      </Card>
    </div>
  );
}
